/**
 * mantle.ts — the pure half of the first-person mantle (build-now wave 6,
 * 2026-09-12; the traversal guide's §10 reduced to what a rig-less body can
 * do): the height band a jump press turns into a climb, and the root
 * profile the capsule follows — RISE first (the torso clears the edge),
 * then the step FORWARD onto the top. The scan itself lives in
 * PhysicsModule.mantleScan (it needs Rapier); this file is what
 * verify:combat / verify:traversal can pin without a world.
 *
 * Why a band: autostep already swallows 0.8 m (the KCC's own setting), so
 * anything lower is a step, not a mantle; above 1.45 m the standing player
 * could not reach the top with a hand — that is a wall, and walls are for
 * the rig that does not exist yet.
 */

/** Obstacle heights (top above the feet) that mantle. Below MIN autostep takes it. */
export const MANTLE_MIN_M = 0.7;
export const MANTLE_MAX_M = 1.45;
/** The scripted climb, in fixed ticks (500 ms at 60 Hz). The forward phase
 *  covers ≈ 1.2 m over its last 65 %: its peak per-tick step stays under a
 *  walk's 0.1 m, so the server's walk-speed integration keeps up and the
 *  reconcile never sees a teleport. */
export const MANTLE_TICKS = 30;
/** How far ahead (from the capsule centre) the wall is looked for. */
export const MANTLE_PROBE_M = 0.9;
/** The landing point sits this far past the obstacle's front face. */
export const MANTLE_LAND_M = 0.45;
/** A face steeper than this (|normal.y| below it) counts as a wall. */
export const MANTLE_WALL_NY_MAX = 0.35;

export type MantleVerdict = { ok: true; why: 'ok' } | { ok: false; why: 'step' | 'too-high' };

/** Is an obstacle of this height (top − feet, metres) a mantle? */
export function classifyMantle(heightM: number): MantleVerdict {
  if (!(heightM >= MANTLE_MIN_M)) return { ok: false, why: 'step' };
  if (heightM > MANTLE_MAX_M) return { ok: false, why: 'too-high' };
  return { ok: true, why: 'ok' };
}

const smooth = (x: number): number => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t); };

/** The root profile at normalised time t ∈ [0, 1]: `rise` (0 → 1 over the
 *  first 60 %) then `forward` (0 → 1 over the last 65 %); both are
 *  smoothsteps, so the climb starts and ends at rest. */
export function mantleProfile(t: number): { rise: number; forward: number } {
  return { rise: smooth(t / 0.6), forward: smooth((t - 0.35) / 0.65) };
}
